import React, { useState, useEffect, useContext } from 'react';
import { Grid, Box, Paper, CircularProgress } from '@mui/material';
import { CheckOutlined, CloudDownload } from '@mui/icons-material';
import ClearIcon from '@mui/icons-material/Clear';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttDialog,
    SttDropdownButton,
    SttFormDate,
    SttFormText,
    SttFormTextarea,
    SttSelectPForm,
    SttSelectUser,
    SttTable,
} from '../../sporttia/all';
import { useCrud, useInteractionsFiles } from '../../lib/hooks';
import Consumption from './boletoDialog/Consumption';
import HandlePaymentsModal from '../../pages/sc/groups/HandlePaymentsModal';
import DataphonePaymentDialog from './DataphonePaymentDialog';
import POSStripePaymentDialog from '../stripe/POSStripePaymentDialog';
import useBonosService from '../../services/BonosService';
import { getErrorMessage } from '../../lib/utils';
import constants from '../../config/constants';
import colors from '../../styles/Colors';
import CashdroControlDialog from './CashdroControlDialog';
import DateBuilder from '../../lib/DateBuilder';
import { PERMISSIONS } from '../../lib/permissions';

type User = {
    id: number;
    name?: string;
    fullName?: string;
};

type Bono = {
    id: number;
    name: string;
    price: number;
};

type Payment = {
    id: number;
    price: number;
    paymentForm: string;
};

type Sportcenter = {
    id: number;
    short?: string;
    address?: string;
    phonePublic?: string;
    customer?: {
        name?: string;
        cif?: string;
    };
};

type Boleto = {
    id?: number;
    user?: User;
    bono?: Bono;
    payment?: Payment;
    price?: number;
    expiration?: string;
    notes?: string;
    consumed?: number;
    amount?: number;
    trash?: boolean;
};

type Movement = {
    id: number;
    created: string;
    concept: string;
    amount: number;
};

type MovementsResponse = {
    rows?: Movement[];
    count?: number;
};

type POSPaymentParams = {
    amount: number;
    concept: string;
    idTpv: number;
    sc: {
        id?: number;
        scName?: string;
        entityName?: string;
        address?: string;
        cif?: string;
        phone?: string;
    };
    returnParams: unknown;
} | null;

type Mship = {
    fullName?: string;
    user?: User;
};

type BoletoDialogProps = {
    item: Boleto;
    onSave?: () => void;
    onClose?: () => void;
};

type PageAndRows = {
    page: number;
    rows: number;
};

type TpvResponse = {
    tpv?: {
        id: number;
        price: number;
        concept: string;
        sc?: Sportcenter;
        payment?: Payment;
    };
};

export default function BoletoDialog({
    item,
    onSave,
    onClose,
}: BoletoDialogProps): React.ReactElement {
    const { t } = useTranslation();
    const cxt = useContext(AppContext)!;
    const { openFile } = useInteractionsFiles();

    const [boleto, setBoleto, setPropertyRaw] = useCrud<Boleto>(item);
    const [movements, setMovements] = useState<MovementsResponse | undefined>();
    const [price, setPrice] = useState<number | undefined>();
    const [paymentForm, setPaymentForm] = useState<string>('');

    // Wrapper to adapt useCrud setProperty to form component onChange signature
    const setProperty = ({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }) => {
        if (!name) return;
        setPropertyRaw({
            name: name as keyof Boleto,
            value: value as Boleto[keyof Boleto],
        });
    };
    const [openAskingPaymentProcedure, setOpenAskingPaymentProcedure] =
        useState(false);

    const [posPaymentParams, setPosPaymentParams] =
        useState<POSPaymentParams>(null);
    const rowsPerPage = 5;
    const [pageAndRows, setPageAndRows] = useState<PageAndRows>({
        page: 1,
        rows: rowsPerPage,
    });
    const { useGetBonoBoletosMovs } = useBonosService();
    const getBonoBoletoMovsQuery = useGetBonoBoletosMovs(
        boleto?.id ?? 0,
        {
            ...pageAndRows,
            trash: !!boleto?.trash,
        },
        {
            enabled: !!boleto?.id,
        },
    );

    useEffect(() => {
        if (getBonoBoletoMovsQuery.data) {
            setMovements(getBonoBoletoMovsQuery.data);
        }
    }, [getBonoBoletoMovsQuery.data]);

    const [cashdroPayment, setCashdroPayment] = useState(false);
    const [cashdroPaymentAmount, setCashdroPaymentAmount] = useState(0);

    const CASHDRO = cxt?.sc?.cashdroConnected;

    useEffect(() => {
        if (item.bono || item.payment) {
            setBoleto(item);
            setPrice(item.payment ? item.payment.price : item.bono?.price);
            setPaymentForm(item.payment ? item.payment.paymentForm : '');
        } else {
            setPrice(item.price);
        }
    }, [item]);

    function saveAndClose(): void {
        onSave?.();
        onClose?.();
    }

    const bonosService = useBonosService();
    const updateBonoBoletoMutation = bonosService.useUpdateBonoBoleto({
        onSuccess: () => {
            saveAndClose();
        },
        onError: (error: unknown) => {
            cxt?.showMessage('E', getErrorMessage(error));
        },
    });
    const createBonoUserMutation = bonosService.useCreateBonoUser({
        onSuccess: (response: unknown) => {
            const { tpv } = response as TpvResponse;

            if (
                tpv?.payment?.paymentForm ===
                constants.payment.paymentForms.dataphoneConnected.name
            ) {
                // params for the POS dialog (datafono fisico)
                setPosPaymentParams({
                    amount: tpv.price ?? 0,
                    concept: tpv.concept ?? '',
                    idTpv: tpv.id ?? 0,
                    sc: {
                        id: tpv?.sc?.id,
                        scName: tpv?.sc?.short,
                        entityName: tpv?.sc?.customer?.name,
                        address: tpv?.sc?.address,
                        cif: tpv?.sc?.customer?.cif,
                        phone: tpv?.sc?.phonePublic,
                    },
                    returnParams: null,
                });
            } else {
                saveAndClose();
            }
        },
        onError: (error: unknown) => {
            cxt?.showMessage('E', getErrorMessage(error));
        },
    });

    const createPayment = (): void => {
        if (!boleto?.bono?.id || !boleto?.user?.id) return;

        createBonoUserMutation.mutate({
            bonoId: boleto.bono.id,
            userId: boleto.user.id,
            params: {
                paymentForm: paymentForm as
                    | 'DATAFONO'
                    | 'DATAFONO_CONNECTED'
                    | 'CASH'
                    | 'PURSE'
                    | 'TPV'
                    | 'LATER'
                    | 'BANK'
                    | 'FREE'
                    | 'RECEIPT'
                    | 'ZITYCARD'
                    | 'CASHDRO',
                price,
                expiration: boleto.expiration,
                notes: boleto.notes ?? '',
            },
        });
    };

    const setupPayment = (): void => {
        if (
            CASHDRO &&
            paymentForm === constants.payment.paymentForms.cashdro.name
        ) {
            setCashdroPayment(true);
            setCashdroPaymentAmount(price ?? 0);
        } else {
            createPayment();
        }
    };

    function upsert(): void {
        if (boleto?.id) {
            updateBonoBoletoMutation.mutate({
                id: boleto.id,
                params: {
                    notes: boleto.notes ?? '',
                },
            });
        } else {
            setupPayment();
        }
    }

    const deleteBoleto = (deletingPaymentProcedure?: number): void => {
        if (!boleto?.id) return;

        const params: { managePayments?: number } = {};
        if (deletingPaymentProcedure !== undefined) {
            params.managePayments = deletingPaymentProcedure;
        }
        cxt?.api('DELETE', `/bonos/boletos/${boleto.id}`, {
            params,
            success: () => {
                onClose?.();
                setOpenAskingPaymentProcedure(false);
                cxt.showMessage('S', t('Deleted'));
            },
        });
    };

    function consume(): void {
        if (!boleto?.id) return;

        cxt?.api('POST', `/bonos/boletos/${boleto.id}/movs`, {
            params: { consumptions: 1 },
            success: () => {
                setBoleto((prev) => ({
                    ...prev,
                    consumed: Math.abs(prev.consumed ?? 0) + 1,
                }));

                getBonoBoletoMovsQuery.refetch();
            },
            error: (error: unknown) => {
                cxt?.showMessage('E', getErrorMessage(error));
            },
        });
    }

    function openReceipt(format: number): void {
        if (item.payment) {
            openFile(
                `/payments/${item.payment.id}.pdf?format=${format}`,
                'application/pdf',
                {},
                `${t('Ticket')}_${item.payment.id}`,
            );
        }
    }

    function refundBonoMov(movId: number): void {
        cxt?.api('DELETE', `/bonos/movs/${movId}`, {
            confirmation: true,
            success: () => {
                // Deshacer una consumición devuelve el uso al bono. Antes se
                // mutaba `boleto` en sitio, así que la barra de usos no se
                // repintaba hasta cerrar y volver a abrir el diálogo.
                setBoleto((prev) => ({
                    ...prev,
                    consumed: Math.max(0, Math.abs(prev.consumed ?? 0) - 1),
                }));
                getBonoBoletoMovsQuery.refetch();
            },
        });
    }

    if (
        createBonoUserMutation.isPending ||
        updateBonoBoletoMutation.isPending ||
        getBonoBoletoMovsQuery.isLoading
    ) {
        return (
            <SttDialog
                maxWidth="md"
                open
                content={
                    <Box display="flex" justifyContent="center" paddingY={15}>
                        <CircularProgress />
                    </Box>
                }
            />
        );
    }

    return (
        <>
            <SttDialog
                open={!!item}
                maxWidth="md"
                title={t('generic.bono')}
                onClose={onClose}
                content={
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12 }}>
                            <SttSelectUser
                                controlDisabled={!!boleto?.id}
                                name="user"
                                caption={t('generic.name')}
                                user={
                                    boleto?.user as Parameters<
                                        typeof SttSelectUser
                                    >[0]['user']
                                }
                                onChange={setProperty}
                                onSelect={(mship: unknown) => {
                                    const mshipData = mship as Mship | null;
                                    if (mshipData) {
                                        setProperty({
                                            name: 'user',
                                            value: {
                                                fullName: mshipData.fullName,
                                                id: mshipData.user?.id ?? 0,
                                            },
                                        });
                                    } else {
                                        setProperty({
                                            name: 'user',
                                            value: undefined,
                                        });
                                    }
                                }}
                                returnMship
                            />
                        </Grid>

                        {/* --- Payment form --- */}
                        {(price ?? 0) > 0 && (
                            <Grid size={{ xs: 4 }}>
                                <SttSelectPForm
                                    name="pForm"
                                    disabled={!!boleto?.id}
                                    defVal={paymentForm}
                                    activePFs={[
                                        '',
                                        constants.payment.paymentForms.purse
                                            .name,
                                        constants.payment.paymentForms.cash
                                            .name,
                                        constants.payment.paymentForms.bank
                                            .name,
                                        cxt?.sc?.dataphoneConnected
                                            ? constants.payment.paymentForms
                                                  .dataphoneConnected.name
                                            : constants.payment.paymentForms
                                                  .dataphone.name,
                                        constants.payment.paymentForms.receipt
                                            .name,
                                        constants.payment.paymentForms.free
                                            .name,
                                    ].concat(
                                        CASHDRO
                                            ? [
                                                  constants.payment.paymentForms
                                                      .cashdro.name,
                                              ]
                                            : [],
                                    )}
                                    onChange={(event: {
                                        name: string | undefined;
                                        value: unknown;
                                    }) => setPaymentForm(String(event.value))}
                                />
                            </Grid>
                        )}
                        <Grid size={{ xs: (price ?? 0) > 0 ? 4 : 12 }}>
                            <SttFormDate
                                name="expiration"
                                caption={t('Expiration')}
                                disabled={!!boleto?.id}
                                defVal={boleto?.expiration}
                                onChange={setProperty}
                            />
                        </Grid>
                        {(price ?? 0) > 0 && (
                            <Grid size={{ xs: 4 }}>
                                <SttFormText
                                    name="price"
                                    caption={t('generic.price')}
                                    disabled={!!boleto?.id}
                                    defVal={String(price ?? '')}
                                    onChange={({
                                        name,
                                        value,
                                    }: {
                                        name: string | undefined;
                                        value: unknown;
                                    }) => {
                                        if (!name) return;
                                        setPrice(value as number);
                                    }}
                                />
                            </Grid>
                        )}
                        <Grid size={{ xs: 12 }}>
                            <SttFormTextarea
                                name="notes"
                                caption={t('generic.notes')}
                                defVal={boleto?.notes}
                                onChange={setProperty}
                            />
                        </Grid>
                        {boleto?.id && (
                            <>
                                <Grid size={{ xs: 12 }}>
                                    <Consumption
                                        caption={`${t(
                                            'Consumed',
                                        )} ${Math.abs(boleto.consumed ?? 0)} / ${
                                            boleto.amount
                                        }`}
                                        amount={boleto.amount ?? 0}
                                        consumed={Math.abs(
                                            boleto.consumed ?? 0,
                                        )}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <Paper>
                                        <Box m={3}>
                                            <SttTable
                                                autoload={false}
                                                data={movements}
                                                localPagination={false}
                                                perPage={5}
                                                columns={[
                                                    {
                                                        title: t(
                                                            'bono.movement',
                                                        ),
                                                        type: 'text',
                                                        value: (
                                                            row: Movement,
                                                        ) => (
                                                            <p
                                                                style={{
                                                                    color:
                                                                        row.amount >
                                                                        0
                                                                            ? colors.green
                                                                            : colors.red,
                                                                }}
                                                            >
                                                                {row.amount > 0
                                                                    ? t(
                                                                          'bono.refund',
                                                                      )
                                                                    : t(
                                                                          'bono.consumption',
                                                                      )}
                                                            </p>
                                                        ),
                                                    },
                                                    {
                                                        title: t(
                                                            'generic.created',
                                                        ),
                                                        field: 'created',
                                                        value: (
                                                            row: Movement,
                                                        ) =>
                                                            new DateBuilder(
                                                                row?.created,
                                                                cxt?.sc
                                                                    ?.timezone
                                                                    ?.name,
                                                                cxt?.lang?.key,
                                                            ).dmyhm(),
                                                    },
                                                    {
                                                        title: t(
                                                            'generic.concept',
                                                        ),
                                                        type: 'text',
                                                        field: 'concept',
                                                    },
                                                    {
                                                        title: t(
                                                            'generic.amount',
                                                        ),
                                                        type: 'text',
                                                        field: 'amount',
                                                    },
                                                    {
                                                        title: '',
                                                        value: (
                                                            row: Movement,
                                                        ) =>
                                                            row.amount < 0 && (
                                                                <ClearIcon
                                                                    sx={{
                                                                        cursor: 'pointer',
                                                                        '&:hover':
                                                                            {
                                                                                color: 'var(--brand-text)',
                                                                            },
                                                                    }}
                                                                    onClick={() =>
                                                                        refundBonoMov(
                                                                            row.id,
                                                                        )
                                                                    }
                                                                />
                                                            ),
                                                    },
                                                ]}
                                                onFetch={(
                                                    response: PageAndRows,
                                                ) => {
                                                    setPageAndRows(response);
                                                }}
                                            />
                                        </Box>
                                    </Paper>
                                </Grid>
                            </>
                        )}
                    </Grid>
                }
                buttons={
                    [
                        boleto?.id &&
                            boleto.payment &&
                            !boleto.trash && {
                                component: (
                                    <SttDropdownButton
                                        icon={<CloudDownload />}
                                        caption={t('generic.print')}
                                        options={[
                                            {
                                                label: 'A4',
                                                value: 0,
                                            },
                                            {
                                                label: 'Ticket',
                                                value: 1,
                                            },
                                        ]}
                                        size="medium"
                                        variant="outlined"
                                        color="inherit"
                                        onSelected={(opt: {
                                            value: string | number;
                                        }) => openReceipt(Number(opt.value))}
                                    />
                                ),
                            },
                        !boleto?.trash && {
                            type: 'save' as const,
                            onClick: upsert,
                            permission: PERMISSIONS.VOUCHER.MODIFY,
                        },
                        boleto?.id &&
                            !boleto.trash && {
                                type: 'delete' as const,
                                onClick: () =>
                                    setOpenAskingPaymentProcedure(true),
                                permission: PERMISSIONS.VOUCHER.DELETE,
                            },
                        boleto?.id &&
                            !boleto.trash && {
                                icon: <CheckOutlined />,
                                caption: t('Consume'),
                                onClick: consume,
                            },
                        {
                            type: 'close' as const,
                            onClick: onClose,
                        },
                    ].filter(Boolean) as Parameters<
                        typeof SttDialog
                    >[0]['buttons']
                }
            />

            <HandlePaymentsModal
                warningText={t('sc.boleto.delete.howToHandlePayments')}
                open={openAskingPaymentProcedure}
                onClose={() => setOpenAskingPaymentProcedure(false)}
                options={[
                    {
                        caption: t('associatedPayments.doNothing'),
                        value: '0',
                    },
                    {
                        caption: t('associatedPayments.refundToPurse'),
                        value: '1',
                    },
                    {
                        caption: t('associatedPayments.refundInCash'),
                        value: '2',
                    },
                    {
                        caption: t('associatedPayments.delete'),
                        value: '3',
                    },
                ]}
                onAccept={(option: string | null) => {
                    switch (option) {
                        case '0':
                            deleteBoleto();
                            break;
                        case '1':
                            deleteBoleto(1);
                            break;
                        case '2':
                            deleteBoleto(2);
                            break;
                        case '3':
                            deleteBoleto(3);
                            break;
                        default:
                    }
                }}
            />

            {cxt?.sc?.sporttiaStripeLocationId && posPaymentParams !== null ? (
                <POSStripePaymentDialog
                    paymentData={posPaymentParams}
                    onClose={() => setPosPaymentParams(null)}
                    onSuccess={() => {
                        saveAndClose();
                    }}
                />
            ) : (
                <DataphonePaymentDialog
                    open={posPaymentParams !== null}
                    paymentData={posPaymentParams}
                    onClose={() => setPosPaymentParams(null)}
                    onSuccess={() => {
                        saveAndClose();
                    }}
                />
            )}

            {cashdroPayment && (
                <CashdroControlDialog
                    open={cashdroPayment}
                    amount={cashdroPaymentAmount}
                    onClose={() => {
                        setCashdroPayment(false);
                        setCashdroPaymentAmount(0);
                    }}
                    onSuccess={() => {
                        setCashdroPayment(false);
                        setCashdroPaymentAmount(0);
                        createPayment();
                    }}
                />
            )}
        </>
    );
}
